import { useEffect, useState } from "react";
import {
  SettingNumber,
  SettingSlider,
  ShortcutPanel,
  VoicePanel,
} from "@cuekards/ui";
import type { MediaStreamLike } from "@cuekards/ui";
import {
  CONSTANT_SPEED_WPM_LIMITS,
  DEFAULT_CONSTANT_SPEED_WPM,
  DEFAULT_OVERLAY,
  OVERLAY_LIMITS,
  OVERLAY_SLIDERS,
  type OverlaySettingsState,
} from "@cuekards/core/overlay-defaults";
import {
  LINE_SPACING,
  MIRROR_TEXT,
  MIRROR_TEXT_HINT,
  lineSpacingValue,
} from "@cuekards/core/prompter-copy";
import { DeviceSettings } from "./DeviceSettings.js";
import { settingsFromTheLink } from "./settings-copy.js";
import { thisPagesSetup } from "./settings-url.js";
import { SettingsTransfer } from "./SettingsTransfer.js";
import { HowItWorksButton } from "./site-links.js";

/**
 * The web's settings, which are two screens' worth on one page.
 *
 * Deliberately not the desktop's prompter screen. More than half of what
 * `OverlayDefaultsPanel` sets — width, height, position, opacity — describes a
 * window, and the tab is the prompter. What is shared is the rows themselves,
 * out of `@cuekards/ui`, so that the two hosts cannot disagree about when a typed
 * number is committed — and, since one-window ticket 02, the whole shortcuts
 * screen, which is now literally the same component on both hosts.
 *
 * Both sections have their own reset. That is FR-64 and it is per screen rather
 * than one button at the bottom, because "put the shortcuts back" and "put the
 * prompter back" are different things to want and mixing them makes the button
 * too frightening to press.
 */
export function SettingsScreen({
  onBack,
  stream,
  showing,
  disabled = false,
}: {
  /**
   * Back to whichever screen this was opened from.
   *
   * `onBack` rather than web-ui ticket 07's `onDone`, and the rename is the
   * control changing rather than a tidy-up: "Done" implies you were filling
   * something in, and nothing on this screen is a form — every row here takes
   * effect as it is moved. See the header below.
   */
  onBack: () => void;
  /**
   * The self-view, for the device section's preview (web-ui ticket 09).
   *
   * The host's one `useCapture` stream, handed down from `WebApp` — the same
   * object the green room's stage and the prompter's backdrop are drawing.
   * Nothing on this screen opens a device, which is what keeps the one
   * `getUserMedia` on this screen the button that asks for the device *names*.
   */
  stream: MediaStreamLike | null;
  /** Whether that stream carries a picture, as against merely a microphone —
   * `WebApp`'s reading, so the thumbnail and the room's stage cannot disagree
   * about whether there is a camera to look at. */
  showing: boolean;
  /** True while a take is in progress: the device picker has no safe outcome
   * mid-take, for the same reason refresh does not (FR-17). */
  disabled?: boolean;
}): JSX.Element {
  const fromTheLink = settingsFromTheLink(thisPagesSetup());

  /**
   * At the top, which stopped being free with web-ui ticket 07.
   *
   * This is the only screen in the product that scrolls the *document*. The help
   * page beside it was the other, and "Change these in Settings →" at the foot of
   * it was a route from the bottom of one to the top of the other — so without
   * this you arrived at Settings at whatever offset the help page was left at,
   * halfway down a screen you had never seen the top of. That page is deleted
   * (flow ticket 02) and the route with it, but a stale offset is a property of
   * the window rather than of whoever moved it last, so this stays: the prompter
   * and the green room never had the problem only because neither of them moves
   * the window at all.
   */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="web-settings">
      {/* `← Back` above the title, which was the help page's arrangement and is
        * this screen's since web-ui ticket 09. Two screens you *leave* ought to be
        * left the same way, and "Back" says where it goes where the "Done"
        * button this replaces implied you had been filling something in.
        *
        * The `?` stays in the corner rather than following the pill: it is the
        * same control in the same place on every screen, and that sameness is
        * the whole of what it buys. It keeps the room's own class, which is the
        * handle the browser drivers press it by.
        *
        * **It is an anchor since flow ticket 02**, to the same `/how-it-works`
        * every document's bar points at, and it is literally the same component
        * the prompter draws — `HowItWorksButton`, so that the one attribute
        * holding the link back from ending a take cannot be true here and false
        * there. It opened `HelpPage` until then: a third rendering of copy that
        * already exists at an address. */}
      <header className="web-settings-head">
        <div className="web-settings-buttons">
          <button type="button" className="web-settings-back" onClick={onBack}>
            ← Back
          </button>
          <HowItWorksButton />
        </div>
        <h1>Settings</h1>
      </header>

      {/* Above every control on the screen, because it is about all of them: a
        * setup from the address is why a font size here may not be the one this
        * browser saved, and why changing it will not save over it. Somewhere
        * further down it would be an explanation arriving after the confusion.
        * Absent entirely on an ordinary visit, which is nearly all of them. */}
      {fromTheLink !== undefined && (
        <p className="web-from-link" role="status">
          {fromTheLink}
        </p>
      )}

      <h2>The prompter</h2>
      <PrompterSettings />

      {/* Web ticket 11. Above the shortcuts because it is the one section a
        * first-time visitor has to visit before their first take, and below the
        * prompter because it is the one they visit once. */}
      <h2>Camera and microphone</h2>
      <DeviceSettings stream={stream} showing={showing} disabled={disabled} />

      <h2>Shortcuts</h2>
      <ShortcutPanel host="web" />

      {/* Web ticket 16, and below the keyboard on purpose: the shortcuts are what
        * anyone at the desk reaches for, and this is what you set up *once* before
        * standing in front of the camera. It is `@cuekards/ui`'s panel unmodified,
        * which is why there is no `<h2>` of ours above it — it brings its own,
        * beside the status that has to sit on the same line as it. That the two
        * hosts cannot drift on the wording is the point: this is the one feature in
        * the product whose failure mode is losing a take. */}
      <VoicePanel />

      {/* Last, and that is the placement the section argues for: it is about
        * everything above it, so it reads as a footnote to a screen you have
        * just been through rather than as an obstacle in front of one. Nobody
        * arrives at the settings screen wanting to export — they arrive wanting
        * to change something, and find out on the way back out that what they
        * changed is this browser's alone. */}
      <h2>Keeping these settings</h2>
      <SettingsTransfer disabled={disabled} />
    </main>
  );
}

/** The state before the worker has answered, so no input is ever uncontrolled. */
const PENDING: OverlaySettingsState = {
  defaults: DEFAULT_OVERLAY,
  wpm: DEFAULT_CONSTANT_SPEED_WPM,
};

/**
 * What a session starts from: the prompt's size and the constant-speed rate.
 *
 * These are the *stored* values, and the distinction is the whole of FR-63. The
 * font shortcuts change the prompter in front of you and leave this alone, so
 * experimenting mid-take cannot cost you the setup you arrived with — and "Save
 * the prompter as it is" is the way to keep a change you liked.
 */
function PrompterSettings(): JSX.Element {
  const [state, setState] = useState<OverlaySettingsState>(PENDING);
  const [saved, setSaved] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void window.cuekards.getOverlaySettings().then((next) => {
      if (!cancelled) setState(next);
    });
    const off = window.cuekards.onOverlaySettingsChanged(setState);
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  // "Save the prompter as it is" is the one control here with no visible effect
  // when the prompter already matches, and silence is indistinguishable from a
  // dead button. So it says so, briefly.
  useEffect(() => {
    if (saved === 0) return;
    const timer = setTimeout(() => setSaved(0), 2500);
    return () => clearTimeout(timer);
  }, [saved]);

  return (
    <section className="settings-screen">
      <p className="settings-note">
        What a new session starts from. The font shortcuts change the prompter in
        front of you without touching these.
      </p>

      {/* The prompter's scrim over the self-view (web-ui ticket 09), and the one
        * row in this section that is not "what a new session starts from": it
        * takes effect as it moves, on a prompter that may be on the other side
        * of the door. That is ADR-0004's live-control rule and it is why the
        * note above says what it says about the *font* shortcuts rather than
        * about the section.
        *
        * Above the font size because it is the one you set once, by eye,
        * against your own room's lighting — and because a wash you cannot see
        * the effect of is the row somebody scrolls past. It carries no reset of
        * its own: `resetLook` already puts it back, which is what makes the
        * button below the way home from a wash dialled to 90 in a dark room. */}
      <SettingSlider
        label="Backdrop"
        value={state.defaults.backdrop}
        min={OVERLAY_LIMITS.minBackdrop}
        max={OVERLAY_LIMITS.maxBackdrop}
        step={OVERLAY_SLIDERS.backdropStep}
        format={(v) => `${v}%`}
        onChange={(backdrop) => {
          // Held optimistically as well as sent, for the font size's reason
          // below: the slider tracks the pointer rather than the round trip.
          setState({ ...state, defaults: { ...state.defaults, backdrop } });
          window.cuekards.setOverlayDefaults({ ...state.defaults, backdrop });
        }}
      />

      <SettingSlider
        label="Font size"
        value={state.defaults.fontSize}
        min={OVERLAY_LIMITS.minFont}
        max={OVERLAY_LIMITS.maxFont}
        step={OVERLAY_SLIDERS.fontStep}
        format={(v) => `${v} px`}
        onChange={(fontSize) => {
          // Held optimistically as well as sent: the worker echoes the same
          // value straight back, so the slider tracks the pointer rather than
          // the round trip.
          setState({ ...state, defaults: { ...state.defaults, fontSize } });
          window.cuekards.setOverlayDefaults({ ...state.defaults, fontSize });
        }}
      />

      {/* Line spacing (prompter-display ticket 04): how far apart the lines are
        * drawn, as a factor on the prompter's own line height, so `1.0×` is the
        * spacing it always had. Beside the font size because the two are judged
        * together: a large font on a small screen wants the lines closer, a
        * small one on a big monitor wants them further apart.
        *
        * Live like the backdrop (ADR-0004): the host tells the prompter on every
        * default it sets. Taste rather than a fact about the rig, so the reset
        * below puts it back. No `⋯` row, shortcut or URL parameter yet. */}
      <SettingSlider
        label={LINE_SPACING}
        value={state.defaults.lineSpacing}
        min={OVERLAY_LIMITS.minLineSpacing}
        max={OVERLAY_LIMITS.maxLineSpacing}
        step={OVERLAY_SLIDERS.lineSpacingStep}
        format={lineSpacingValue}
        onChange={(lineSpacing) => {
          // Held optimistically as well as sent, for the sliders' reason.
          setState({ ...state, defaults: { ...state.defaults, lineSpacing } });
          window.cuekards.setOverlayDefaults({ ...state.defaults, lineSpacing });
        }}
      />

      {/* Mirror text (prompter-display ticket 02): the script drawn backwards,
        * for a screen read through teleprompter glass. Live like the backdrop
        * (ADR-0004): the host tells the prompter on every default it sets.
        *
        * The only control for it. There is no `⋯` row, no shortcut and no URL
        * parameter: a rig is set up once, and a link would carry the sender's
        * glass to somebody without any. And `resetLook` leaves it alone, because
        * whether there is glass in front of the lens is not a matter of taste.
        *
        * The repository's checkbox pattern: a native input inside its label,
        * the words as a sibling. */}
      <label className="web-settings-toggle">
        <input
          type="checkbox"
          checked={state.defaults.mirror}
          onChange={(event) => {
            const mirror = event.target.checked;
            // Held optimistically as well as sent, for the sliders' reason.
            setState({ ...state, defaults: { ...state.defaults, mirror } });
            window.cuekards.setOverlayDefaults({ ...state.defaults, mirror });
          }}
        />
        {MIRROR_TEXT}
      </label>
      <p className="settings-note">{MIRROR_TEXT_HINT}</p>

      <SettingNumber
        label="Constant-speed rate"
        unit="wpm"
        value={state.wpm}
        min={CONSTANT_SPEED_WPM_LIMITS.min}
        max={CONSTANT_SPEED_WPM_LIMITS.max}
        onCommit={(raw) => {
          // An empty box means "leave it", not "the slowest rate on the dial".
          if (raw.trim() === "") return;
          window.cuekards.setConstantSpeedWpm(Number(raw));
        }}
      />

      <div className="settings-actions">
        <button
          type="button"
          onClick={() => {
            window.cuekards.saveOverlayAsDefaults();
            setSaved((n) => n + 1);
          }}
          title="Make the prompter's current font size the one new sessions start from"
        >
          Save the prompter as it is
        </button>
        {saved > 0 && (
          <span className="settings-ack" role="status">
            ✓ Saved
          </span>
        )}
        {/* Never greyed, even when these already are the built-ins: reset also
          * puts them *on* the prompter, so it is the way back from a font size
          * dialled somewhere unreadable. */}
        <button
          type="button"
          className="settings-reset"
          onClick={() => window.cuekards.resetOverlaySettings()}
          title="Restore the built-in prompter settings and apply them"
        >
          Reset to defaults
        </button>
      </div>
    </section>
  );
}
